import { ofetch } from 'ofetch';
import { env, API_TIMEOUT_MS } from '@noor/config';
import { ApiError } from './error';
import { normalizeErrorBody } from './error-body';
import { refreshAuthToken } from './auth-token';
import { logApiRequest, logApiResponse, resolveRequestUrl } from './request-log';
import { unwrapData } from './unwrap-data';

/**
 * Renew the shared session through the auth service, outside the `http` /
 * `legacyHttp` retry wrappers — for callers that cannot go through those
 * clients (the route polyline's bare fetch: different host, own token
 * fallback).
 *
 * Single-flight across the whole app: when an expiry window 401s several
 * calls at once (an eval and a route lookup racing the same pin drop), all
 * of them await the ONE refresh this triggers rather than each racing its
 * own (see `refreshAuthToken`).
 *
 * Returns the new access token, or `null` when there is no refresh token or
 * the refresh itself fails. Unlike the http clients it NEVER redirects to
 * login — the caller decides: data calls bounce, decoration calls (routing)
 * stay silent.
 */
export async function refreshSessionTokens(): Promise<string | null> {
  return refreshAuthToken(async (refreshToken) => {
    const raw = await authClient<unknown>('/api/v1/auth/refresh', {
      method: 'POST',
      body: { refresh_token: refreshToken },
    });
    const payload = unwrapData(raw) as Partial<{
      access_token: string;
      refresh_token: string;
    }> | null;
    const accessToken = payload?.access_token;
    const newRefreshToken = payload?.refresh_token;
    if (typeof accessToken !== 'string' || typeof newRefreshToken !== 'string') {
      throw new ApiError({ message: 'Malformed refresh response', status: 500 });
    }
    return { accessToken, refreshToken: newRefreshToken };
  });
}

/**
 * Bare client for the auth service. Deliberately WITHOUT the session
 * interceptor: the refresh token travels in the body, and the endpoint is
 * unauthenticated — attaching the (expired) access token can only get the
 * call rejected before its argument is read.
 */
const authClient = ofetch.create({
  baseURL: env.VITE_AUTH_API_URL,
  retry: 0,
  timeout: API_TIMEOUT_MS,
  onRequest({ request, options }) {
    // Dev-only wire log (URL + headers) — silent in prod and under tests.
    logApiRequest(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_AUTH_API_URL),
      new Headers(options.headers),
    );
  },
  onResponse({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_AUTH_API_URL),
      response.status,
    );
  },
  onResponseError({ request, response, options }) {
    logApiResponse(
      options.method ?? 'GET',
      resolveRequestUrl(request, options, env.VITE_AUTH_API_URL),
      response.status,
    );
    const body = normalizeErrorBody(response._data);
    throw new ApiError({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' must fall through
      message: body.message || response.statusText || 'Request failed',
      status: response.status,
      ...(body.code !== undefined ? { code: body.code } : {}),
      ...(body.details !== undefined ? { details: body.details } : {}),
    });
  },
});
